import { useState, useCallback, useEffect } from 'react';
import {
  SchoolMealFormData,
  MealPreferences,
  SaveFormResponseRequest,
} from '../types/form';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const initialFormData: SchoolMealFormData = {
  studentInfo: {
    studentId: '',
    studentFirstName: '',
    studentLastName: '',
    parentName: '',
    studentGrade: '',
  },
  mealPreferences: {
    breakfast: false,
    lunch: false,
    afternoonSnack: false,
  },
  dietaryRestrictions: {
    selected: [],
  },
};

export const useSchoolMealForm = () => {
  const { formData: initialFormDataFromAuth } = useAuth();

  const [formData, setFormData] = useState<SchoolMealFormData>(
    () => initialFormDataFromAuth ?? initialFormData
  );

  // Auth loads meal responses asynchronously after consents-first navigation; keep local state in sync.
  useEffect(() => {
    setFormData(initialFormDataFromAuth ?? initialFormData);
  }, [initialFormDataFromAuth]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const updateMealPreference = useCallback((meal: keyof MealPreferences, value: boolean) => {
    setFormData((prev) => ({
      ...prev,
      mealPreferences: { ...prev.mealPreferences, [meal]: value },
    }));
  }, []);

  const toggleDietaryRestriction = useCallback((restriction: string) => {
    setFormData((prev) => {
      const isSelected = prev.dietaryRestrictions.selected.includes(restriction);
      return {
        ...prev,
        dietaryRestrictions: {
          ...prev.dietaryRestrictions,
          selected: isSelected
            ? prev.dietaryRestrictions.selected.filter((r) => r !== restriction)
            : [...prev.dietaryRestrictions.selected, restriction],
        },
      };
    });
  }, []);

  const addCustomRestriction = useCallback((restriction: string) => {
    const trimmedRestriction = restriction.trim();
    // Validate: must not be empty, must not contain commas, and must not already exist
    if (trimmedRestriction && !trimmedRestriction.includes(',') && !formData.dietaryRestrictions.selected.includes(trimmedRestriction)) {
      setFormData((prev) => ({
        ...prev,
        dietaryRestrictions: {
          ...prev.dietaryRestrictions,
          selected: [...prev.dietaryRestrictions.selected, trimmedRestriction],
        },
      }));
      return true;
    }
    return false;
  }, [formData.dietaryRestrictions.selected]);

  const removeCustomRestriction = useCallback((restriction: string) => {
    setFormData((prev) => ({
      ...prev,
      dietaryRestrictions: {
        ...prev.dietaryRestrictions,
        selected: prev.dietaryRestrictions.selected.filter((r) => r !== restriction),
      },
    }));
  }, []);

  const clearError = useCallback(() => {
    setSubmitError(null);
  }, []);

  const clearSuccess = useCallback(() => {
    setSubmitSuccess(false);
  }, []);

  const submit = useCallback(async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      const payload: SaveFormResponseRequest = {
        formResponse: {
          // itemId is omitted for now; backend can treat as new or infer from auth
          formData: {
            studentInfo: formData.studentInfo,
            mealPreferences: formData.mealPreferences,
            dietaryRestrictions: formData.dietaryRestrictions.selected,
          },
        },
      };

      const response = await apiService.submitSchoolMealForm(payload);
      
      // If success is explicitly false, treat as error
      // Otherwise, if we got here without an exception, treat as success
      if (response.success === false) {
        setSubmitError(response.message || 'Failed to submit form');
        return false;
      } else {
        setSubmitSuccess(true);
        return true;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred while submitting the form';
      setSubmitError(errorMessage);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [formData]);

  return {
    formData,
    isSubmitting,
    submitError,
    submitSuccess,
    updateMealPreference,
    toggleDietaryRestriction,
    addCustomRestriction,
    removeCustomRestriction,
    submit,
    clearError,
    clearSuccess,
  };
};
